import { Box, Button, CircularProgress, DialogActions, Divider } from '@material-ui/core';
import { Dispatch } from '@reduxjs/toolkit';
import CustomSelect from 'components/Form/Input/CustomSelect/CustomSelect';
import { ButtonVariantEnum } from 'enums/buttonVariant';
import { DialogVariantEnum } from 'enums/dialogVariant';
import React, { Component, Fragment } from 'react';
import { Field, Form } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { dialogClose } from 'store/dialog/dialog.actions';
import { DialogOpenPayloadJobs } from 'store/dialog/dialog.interface';
import { modifyLanguages } from 'store/projects/projects.actions';
import { ModifyLanguagesPayload } from 'store/projects/projects.interface';
import { getAvailableLanguagesSelector } from 'store/settings/settings.selectors';
import { getJobActionSpinnerSelector } from 'store/spinner/spinner.selectors';
import { IAutocompleteField } from 'types/shared';
import { requiredAutocomplete } from 'utils/customValidators';
import { KontentLanguageDTO } from 'utils/restApplicationClient';
import { CustomDialogButtonContainer } from '../CustomDialog.styled';

interface IProps {
  onClose: ((response: ButtonVariantEnum) => void) | undefined;
  variant: DialogVariantEnum.ADD_LANGUAGES | DialogVariantEnum.REMOVE_LANGUAGES;
  projectId: string;
  projectLanguages: Array<string>;
  jobs: DialogOpenPayloadJobs;
}

interface IDispatchProps {
  modifyLanguages: (payload: ModifyLanguagesPayload) => AppDispatch;
  dialogClose: () => AppDispatch;
}

interface IStateProps {
  jobActionSpinner: boolean;
  availableLanguages?: Array<KontentLanguageDTO>;
}

type PropType = IProps & WithTranslation & IDispatchProps & IStateProps;

class InTranslation extends Component<PropType> {
  private handleClose = (): void => {
    const { dialogClose } = this.props;
    dialogClose();
  };

  mapProjectLanguages = (): Array<IAutocompleteField> => {
    const { projectLanguages, availableLanguages } = this.props;
    return projectLanguages.map((language) => ({
      label: availableLanguages?.find(({ codeName }) => language === codeName)?.name || '',
      value: language,
    }));
  };

  getLanguageOptions = (): Array<IAutocompleteField> => {
    const { availableLanguages, variant, projectLanguages } = this.props;
    const isAddLanguages = variant === DialogVariantEnum.ADD_LANGUAGES;

    if (isAddLanguages) {
      return (
        availableLanguages
          ?.filter(({ codeName }) => !projectLanguages.includes(codeName))
          .map(({ name: label, codeName: value }) => ({ label, value })) || []
      );
    }
    return this.mapProjectLanguages();
  };

  handleSubmit = ({ languages }: { languages: Array<{ value: string; label: string }> }): void => {
    const { modifyLanguages, variant, projectId } = this.props;
    const isAddLanguages = variant === DialogVariantEnum.ADD_LANGUAGES;

    modifyLanguages({
      projectId,
      languages: languages.map((language) => language.value),
      operation: isAddLanguages ? 'ADD' : 'REMOVE',
    });
  };

  render(): JSX.Element {
    const { jobActionSpinner, t } = this.props;
    const languageOptions = this.getLanguageOptions();

    return (
      <Form
        onSubmit={this.handleSubmit}
        subscription={{
          pristine: true,
          submitting: true,
          errors: true,
        }}
        render={({ handleSubmit }): JSX.Element => (
          <form onSubmit={handleSubmit}>
            <Fragment>
              <Box width="300px" padding="0 20px" paddingBottom="20px">
                <Field
                  name="languages"
                  component={CustomSelect}
                  validate={requiredAutocomplete}
                  multi
                  options={languageOptions}
                  placeholder={t('common.selectPlaceholder')}
                />
              </Box>
              <Divider />
              <DialogActions>
                <CustomDialogButtonContainer>
                  <Button variant="contained" color="secondary" type="submit" disabled={jobActionSpinner}>
                    {(jobActionSpinner && <CircularProgress size="24px" color="secondary" />) || t('common.save')}
                  </Button>
                  <Button variant="outlined" onClick={this.handleClose} disabled={jobActionSpinner}>
                    {t('common.cancel')}
                  </Button>
                </CustomDialogButtonContainer>
              </DialogActions>
            </Fragment>
          </form>
        )}
      />
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  jobActionSpinner: getJobActionSpinnerSelector(state),
  availableLanguages: getAvailableLanguagesSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  modifyLanguages: (payload: ModifyLanguagesPayload): AppDispatch => dispatch(modifyLanguages(payload)),
  dialogClose: (): AppDispatch => dispatch(dialogClose()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(InTranslation));
